<template>
  <!-- Page Content -->
  <div class="d-flex flex-column" style="width: 100%; height: 100%;">
    <!-- Row: Chart -->
    <div
      class="row f-row"
      style="width: 100%; height: 100%; position: relative;"
    >
      <!-- Row: legend -->
      <div class="row f-row container-legends--model-forecast">
        <div class="col col-10 text-left">
          <table
            class="table table-sm table-responsive table-borderless table-market-legend"
            style="width: 100%; background: rgba(0,0,0,0)"
          >
            <tbody>
              <tr>
                <td class="text-left">
                  <div class="triangle-topleft"></div>
                  <span class="inline-ul ml-1">UP/DOWN</span>
                </td>
                <td v-if="dataForecastFilter.length > 0">
                  <i class="fa fa-minus mr-1" style="color:var(--expo)"></i
                  >{{ commodityDefs }} Forecast
                </td>
                <td>
                  <i class="fa fa-minus mr-1" style="color:var(--fwd)"></i>
                  Model Forecast
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        class="row f-row"
        style="position: absolute; left: 50px; top: 35px;z-index: 0;"
      >
        <div class="text-left" style="width: 200px;">
          <table
            class="table table-sm table-borderless table-market-legend"
            style="background: rgba(0,0,0,0)"
          >
            <tbody>
              <tr>
                <td class="txt-label-primary-5 text-left" style="width: 5%">
                  Date:
                  <div
                    id="historical--date"
                    class="candle-hover-info text-left"
                  >
                    {{ settings.date }}
                  </div>
                </td>
                <td class="txt-label-primary-5 text-left" style="width: 5%">
                  Close:
                  <div
                    id="historical--close"
                    class="candle-hover-info text-left"
                  >
                    {{ settings.close }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="col col-12" style="height:100%; width:100%">
        <div
          class="h-100 d-flex flex-column"
          id="model-forecast-div"
          style="width: 100%;"
        >
          <svg id="svg-model-forecast"></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { additionalYSpace, noLiquidity } from "@/_core/_helpers/_helpers";
import {
  colorFwdF,
  colorSymmetryH,
  dnCandles,
  grayLightBackground,
  colorExpo,
  colorDarkExpo,
  candleAlpha,
  colorExpoAlpha,
} from "../../../_core/_helpers/_parametric_tools";
import { select } from "d3";
import { hoverModelFcst } from "./hover.js";
import { Chart } from "@/_core/refactor/Chart";

export default {
  name: "ModelForecast",
  props: {
    chartDataRaw: { required: true },
    dataForecast: { required: true, default: () => [] },
    dataModel: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    processData: { required: true },
    showLoadingHistorical: { required: true },
    showLoadingAll: { required: true },
    comparedAssetLong: { required: false, default: "" },
    commodityDefs: { required: true, default: "na" },
  },
  data() {
    return {
      loading: {
        local: false,
      },
      chartElements: {
        D3ChartModel: null,
        svgNodeModel: null,
        D3HoverCandles: null,
      },
      settings: {
        modelColor: colorFwdF,
        historicalColor: colorSymmetryH,
        dnCandles: dnCandles,
        upCandles: colorExpo,
        dnAlpha: candleAlpha,
        upAlpha: candleAlpha,
        historicalSelectColor: grayLightBackground,
        dateFormat: "%d %b %y",
        currencyFormat: ",.4r",
        date: "-",
        close: "-",
        isCandles: null,
      },
    };
  },
  created() {
    // ===== create initial charts instances =====
    this.chartElements.D3ChartModel = new Chart("model-forecast");
    this.loading.local = true;
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.createChart();
    });
  },
  watch: {
    colorTheme() {
      this.callWidth();
      this.createChart();
    },
    chartDataRaw() {
      this.createChart();
    },
    dataForecast() {
      this.createChart();
    },
    dataModel() {
      this.createChart();
    },
    "loading.local": {
      handler() {
        this.createChart();
      },
      deep: true,
    },
    showLoadingHistorical() {
      this.createChart();
    },
    showLoadingAll() {
      this.createChart();
    },
    commodityDefs() {
      this.createChart();
    },
    comparedAssetLong: {
      handler() {
        this.createChart();
      },
    },
  },
  computed: {
    dataForecastFilter() {
      return this.dataForecast.filter((d) => d.name === this.commodityDefs);
    },
    close() {
      return this.chartDataRaw.at(-1).close;
    },
  },
  methods: {
    deleteChart() {
      select("#model-forecast-div")
        .selectAll(".tooltip")
        .remove();
      select("#model-forecast-div")
        .select("svg > *")
        .remove();
    },
    createChart() {
      if (this.dataForecast.length > 0) {
        this.loading.local = false;
        this.createHistChart();
      } else this.deleteChart();
    },
    createHistChart() {
      if (this.dataForecast.length === 0) return;
      // === set SVG canvas"D3ChartModel"
      this.chartElements["D3ChartModel"].setSVG({
        top: { perc: 0.025, px: 50 },
        right: { perc: 0.4, px: 45 },
        bottom: { perc: 0.5, px: 25 },
        left: { perc: 0.4, px: 45 },
        colorTheme: this.colorTheme,
      });

      if (
        this.chartElements["D3ChartModel"].svg.height === 0 ||
        this.chartElements["D3ChartModel"].svg.width === 0
      ) {
        return;
      }
      // ==== zoom setup
      this.chartElements["D3ChartModel"].enableXZoom(
        "chartBodyModelFcst",
        "clipModelFcst",
      );

      const close1 = "close";
      const close2 = this.commodityDefs;
      const close3 = "mean";

      // === minMax
      const minMaxYAxis = this.getMinMaxYAxis(close1, close2, close3);
      const allDates = [
        ...this.chartDataRaw.map((d) => d.date),
        ...this.dataForecastFilter.map((d) => d.date),
        ...this.dataModel.map((d) => d.date),
      ];
      const seenDates = new Set();
      const xRange = allDates
        .filter((date) => {
          const t = +date;
          if (seenDates.has(t)) return false;
          seenDates.add(t);
          return true;
        })
        .sort((a, b) => +a - +b);

      // Set Axis
      this.chartElements["D3ChartModel"].setDoubleAxisDateLin({
        xRange,
        minMaxYAxis,
        minMax2YAxis: minMaxYAxis,
        color1: this.settings.historicalSelectColor(this.colorTheme),
        color2: this.settings.historicalSelectColor(this.colorTheme),
      });

      // === add historical candles/linesArea
      if (this.settings.isCandles == null) {
        this.settings.isCandles = noLiquidity(this.chartDataRaw);
      }

      if (!this.settings.isCandles) {
        this.chartDataRaw.forEach((d) => (d.zero = minMaxYAxis[0]));

        this.chartElements["D3ChartModel"].addLines({
          type: "linesArea",
          data: this.chartDataRaw,
          upperLineInfo: {
            color: colorExpo(this.colorTheme),
            upperName: "close",
            width: 1.5,
          },
          lowerLineInfo: {
            color: "rgba(0,0,0,0)",
            lowerName: "zero",
          },
          areaColor: colorExpoAlpha(this.colorTheme),
          xIsDate: true,
        });
      } else {
        this.chartElements["D3ChartModel"].addCandles({
          type: "candles",
          data: this.chartDataRaw,
          upColor: this.settings.upCandles(this.colorTheme),
          dnColor: this.settings.dnCandles(this.colorTheme),
          upColorBackground: this.settings.upCandles(this.colorTheme),
          dnColorBackground: this.settings.dnCandles(this.colorTheme),
          opacityUp: 0.6,
          opacityDn: 0.6,
          customName: "TA",
          bandWidthAux: 1,
          addId: true,
          xIsDate: true,
          idField: "date",
          oldChart: false,
        });
      }

      // == Line Model Forecast
      this.chartElements["D3ChartModel"].addLines({
        type: "line2Axis",
        data: this.dataModel,
        color: this.settings.modelColor(this.colorTheme),
        lineWidth: 1.5,
        fieldMame: close2,
        xIsDate: true,
        fieldLine: close2,
      });

      // == Line asset Forecast
      if (this.dataForecastFilter.length > 0) {
        const newData = {
          date: new Date(),
          name: close2,
          mean: this.close,
        };
        if (
          !this.dataForecastFilter.some((item) => +item.date === +newData.date)
        ) {
          this.dataForecastFilter.unshift(newData);
        }
        this.chartElements["D3ChartModel"].addLines({
          type: "line2Axis",
          data: this.dataForecastFilter,
          color: this.settings.historicalColor(this.colorTheme),
          lineWidth: 1.5,
          fieldMame: close3,
          xIsDate: true,
          fieldLine: close3,
        });
      }

      //  Add hovers
      this.chartElements["D3ChartModel"].addHovers({
        colorTheme: this.colorTheme,
        hLine: true,
        vLine: true,
      });
      this.chartElements["D3ChartModel"].createToolTipsHover(
        ["y1-2axis", "x", "y2-2axis", "y-inside"],
        {
          currencyFormat: this.settings.currencyFormat,
          colorTheme: this.colorTheme,
          xTooltipWidth: 60,
          yTooltipWidth: 40,
          yInsideTooltipWidth: 180,
          colorY2AxisTooltip: colorDarkExpo,
          colorYTooltip: colorDarkExpo,
        },
      );
      const createDots = (strokeColor, i) => {
        this.chartElements["D3ChartModel"].addDotHovers({
          type: "dot",
          strokeColor,
          i,
        });
      };
      createDots(colorFwdF(this.colorTheme), 0);
      createDots(colorDarkExpo(this.colorTheme), 1);

      if (this.settings.isCandles) {
        this.chartElements["D3ChartModel"].createBarHover();
      } else {
        createDots(colorExpo(this.colorTheme), 2);
      }
      this.chartElements.svgNodeModel = this.chartElements["D3ChartModel"].svg;
      hoverModelFcst(
        this.chartElements["D3ChartModel"],
        this,
        this.chartDataRaw,
        this.dataModel,
        this.dataForecastFilter,
        "TA",
        this.settings.dateFormat,
        this.settings.currencyFormat,
        close2,
        close3,
      );
    },
    getMinMaxYAxis(close1, close2, close3) {
      let minMaxYAxis = [0, 1];

      minMaxYAxis = this.processData.minMax(this.chartDataRaw, close1, close1);
      minMaxYAxis = this.processData.minMax(
        this.dataModel,
        close2,
        close2,
        minMaxYAxis,
      );
      minMaxYAxis = this.processData.minMax(
        this.dataForecastFilter,
        close3,
        close3,
        minMaxYAxis,
      );
      minMaxYAxis = additionalYSpace(minMaxYAxis, 0.1);
      return minMaxYAxis;
    },
    callWidth() {
      this.deleteChart();
      if (this.dataForecast.length > 0) {
        this.createHistChart();
      }
    },
  },
};
</script>

<style scoped>
.container-legends--model-forecast {
  position: absolute;
  top: 15px;
  left: 50px;
  z-index: 0;
}
/* @media (min-height: 800px) {
  .container-legends--model-forecast {
    top: 15px;
  }
} */
</style>
